import {
  EventType,
  Mutation,
  OfferSource,
  PerkSectionsDataItemsResultTypenames,
  Query,
  SaveKardAttributionEventResultTypenames,
  VendorItem,
} from '@varo/graphql-schema-types';
import { useCallback } from 'react';
import SaveKardAttribution from 'services/graphql/mutations/Cashback/SaveKardAttribution';
import {
  AbortError,
  useCancellableMutation,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useGetProfileInfo from 'shared/hooks/useGetProfileInfo/useGetProfileInfo';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

export type KardMedium = 'BROWSE' | 'MAP' | 'SEARCH';

const logError = (message: unknown) => {
  logErrorWithContext(message, {
    featureArea: FeatureArea.REWARDS,
    responsibleTeam: ResponsibleTeam.CUSTOMER_ACQUISITION,
    severity: ErrorSeverity.LOW,
  });
};

const useKardAttribution = () => {
  const [mutationResult, executeMutation] =
    useCancellableMutation<Mutation>(SaveKardAttribution);
  const { result } = useGetProfileInfo('cache-and-network');

  const { data: mutationData, error: mutationError } = mutationResult;

  const recordImpressions = useCallback(
    async (eventType: EventType, userEvent: Record<string, unknown>) => {
      try {
        await executeMutation({
          input: { eventType: eventType, userEvent: JSON.stringify(userEvent) },
        });
      } catch (error) {
        if (error instanceof AbortError) {
          return;
        }
      }
    },
    [executeMutation],
  );

  const formatRewardsDataForKardImpressions = useCallback(
    (rewardsData: Query, medium: KardMedium) => {
      const typeIds: string[] = [];
      const edges = rewardsData?.myPartnerOffers?.perkSections?.edges;
      edges?.forEach((edge) => {
        edge.node.itemsV2?.forEach((item) => {
          if (
            item.__typename ===
              PerkSectionsDataItemsResultTypenames.SectionItems &&
            item.offerSource === OfferSource.Kard &&
            item.typeId
          ) {
            typeIds.push(item.typeId);
          }
        });
      });

      return {
        data: [
          {
            type: 'attribution',
            attributes: {
              userId: result?.userId ?? '',
              type: 'OFFER',
              medium,
              subtype: 'IMPRESSION',
              typeId: typeIds.join(','),
            },
          },
        ],
      };
    },
    [result?.userId],
  );

  const formatVendorDataForKardImpressions = useCallback(
    (vendors: VendorItem[], medium: KardMedium) => {
      const typeId = vendors.map((vendor) => vendor.id).join(',');

      return {
        data: [
          {
            type: 'attribution',
            attributes: {
              userId: result?.userId ?? '',
              type: 'OFFER',
              medium,
              subtype: 'IMPRESSION',
              typeId,
            },
          },
        ],
      };
    },
    [result?.userId],
  );

  const formatOfferViewData = useCallback(
    (offerId: string, medium: KardMedium) => {
      if (!result?.userId) {
        return null;
      }

      return {
        data: [
          {
            type: 'attribution',
            attributes: {
              userId: result?.userId,
              type: 'OFFER',
              medium,
              subtype: 'VIEW',
              typeId: offerId,
            },
          },
        ],
      };
    },
    [result?.userId],
  );

  if (
    mutationData?.saveKardAttributionEvent?.__typename ===
      SaveKardAttributionEventResultTypenames.SaveKardAttributionEventSuccess &&
    mutationData.saveKardAttributionEvent.success === false
  ) {
    logError('Error saving kard attribution event');
  } else if (
    mutationData?.saveKardAttributionEvent?.__typename ===
    SaveKardAttributionEventResultTypenames.SaveKardAttributionEventError
  ) {
    logError(mutationData.saveKardAttributionEvent.message);
  } else if (mutationError) {
    logError(mutationError);
  }

  return {
    recordImpressions,
    mutationResult,
    formatRewardsDataForKardImpressions,
    formatVendorDataForKardImpressions,
    formatOfferViewData,
  };
};

export default useKardAttribution;
